import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Fade , Collapse } from '@mui/material';

interface BulkModalProgressBarProps {
  total: number;
  current: number;
}

const BulkModalProgressBar = ({ total, current }: BulkModalProgressBarProps) => {
  const [showBar, setShowBar] = useState(total > 0 && current !== total);

  useEffect(() => {
    setTimeout(() => setShowBar(current < total), 1500);
  }, [current, total]);

  if (isNaN(total) || isNaN(current)) return null;
  return (
    <Collapse in={showBar} timeout={1500}>
      <Fade in={showBar} timeout={1500}>
        <Wrapper>
          <Label>
            Processed {current?.toLocaleString()} of {total?.toLocaleString()} jobs
          </Label>
          <ProgressBar>
            <Values width={`${(current / total) * 100}%`} />
          </ProgressBar>
        </Wrapper>
      </Fade>
    </Collapse>
  );
};

export default BulkModalProgressBar;

const Wrapper = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

const Label = styled.div`
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  opacity: 0.5;
`;

const ProgressBar = styled.div`
  height: 8px;
  width: 100%;
  position: relative;
  border-radius: 5px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  background-image: ${({ theme }) => {
    const baseColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400];
    const lightColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300];
    return `linear-gradient(-45deg, ${baseColor} 25%, ${lightColor} 0, ${lightColor} 50%, ${baseColor} 0, ${baseColor} 75%, ${lightColor} 0, ${lightColor})`;
  }};
  background-size: 16px 16px;


  -webkit-animation: f 2s linear infinite;
  animation: f 2s linear infinite;

  @-webkit-keyframes f {
    0% {
      background-position: -2px 0;
    }
    100% {
      background-position: 30px 0; 
  }

  @keyframes f {
    0% {
      background-position: -2px 0; 
    }
    100% {
      background-position: 30px 0; 
    }
  }
`;

const Values = styled.div<{ width: string }>`
  height: 100%;
  transition: width 1.5s, opacity 3s, background 3s;
  position: absolute;
  overflow: hidden;
  box-shadow: 5px 0px 5px 0px rgba(0, 0, 0, 0.5);
  width: ${({ width }) => width};
  background: ${({ theme }) =>
    `linear-gradient(-45deg, ${theme.palette.primary.main}, ${theme.palette.success.main}, ${theme.palette.primary.main}, ${theme.palette.success.main})`};
  background-size: 400% 400%;

  animation: gradient 15s ease infinite;

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;
